import { useState, useEffect, useCallback } from "react";
import {
  Link,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { schoolsAPI } from "./schoolsAPI";
import SchoolCard from "../../components/SchoolCard";
import Pagination from "../../components/Pagination";
import { useSearch } from "../../hooks/useSearch";

export default function SchoolList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchQuery } = useSearch();

  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    level: "",
    sortBy: "name",
    sortOrder: "asc",
    page: 1,
    limit: 12,
  });
  const [pagination, setPagination] = useState({
    totalPages: 0,
    currentPage: 1,
    total: 0,
  });
  const [inputPage, setInputPage] = useState(1);

  const schoolLevels = [
    { value: "", label: "Tất cả cấp học" },
    { value: "primary", label: "Tiểu học" },
    { value: "secondary", label: "Trung học cơ sở" },
    { value: "high", label: "Trung học phổ thông" },
    { value: "university", label: "Đại học" },
  ];

  const sortOptions = [
    { value: "name-asc", label: "Tên A-Z" },
    { value: "name-desc", label: "Tên Z-A" },
    { value: "rating-desc", label: "Đánh giá cao nhất" },
    { value: "rating-asc", label: "Đánh giá thấp nhất" },
  ];

  // Đồng bộ search query từ URL params
  useEffect(() => {
    const searchFromUrl = searchParams.get("search") || "";
    const levelFromUrl = searchParams.get("level") || "";
    const sortFromUrl = searchParams.get("sortBy") || "name";
    const sortOrderFromUrl = searchParams.get("sortOrder") || "asc";
    const pageFromUrl = parseInt(searchParams.get("page")) || 1;

    setFilters((prev) => ({
      ...prev,
      search: searchFromUrl,
      level: levelFromUrl,
      sortBy: sortFromUrl,
      sortOrder: sortOrderFromUrl,
      page: pageFromUrl,
    }));
  }, [searchParams]);

  // Đồng bộ search từ header context
  useEffect(() => {
    if (searchQuery !== filters.search) {
      setFilters((prev) => ({
        ...prev,
        search: searchQuery,
        page: 1,
      }));

      // Cập nhật URL params
      const newParams = new URLSearchParams(searchParams);
      if (searchQuery) {
        newParams.set("search", searchQuery);
      } else {
        newParams.delete("search");
      }
      newParams.set("page", "1");
      setSearchParams(newParams);
    }
  }, [searchQuery, filters.search, searchParams, setSearchParams]);

  // Fetch data với debounce
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        // Làm sạch filters trước khi gửi API
        const cleanFilters = {};
        Object.keys(filters).forEach((key) => {
          if (filters[key] && filters[key] !== "") {
            cleanFilters[key] = filters[key];
          }
        });

        const result = await schoolsAPI.getSchools(cleanFilters);

        if (result.success) {
          setSchools(result.data.schools);
          setPagination({
            totalPages: result.data.totalPages,
            currentPage: result.data.currentPage,
            total: result.data.total,
          });
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError("Đã xảy ra lỗi không mong muốn");
      } finally {
        setLoading(false);
      }
    };

    // Chỉ debounce cho search, không debounce cho page change
    const isPageChange = filters.page !== 1 && !filters.search;
    const delay = isPageChange ? 0 : 300;

    const timer = setTimeout(fetchData, delay);
    return () => clearTimeout(timer);
  }, [filters]);

  const handleFilterChange = (key, value) => {
    let newFilters;

    // Xử lý đặc biệt cho sort option
    if (key === "sort") {
      const [sortBy, sortOrder] = value.split("-");
      newFilters = {
        ...filters,
        sortBy,
        sortOrder,
        page: 1,
      };
    } else {
      newFilters = {
        ...filters,
        [key]: value,
        page: 1,
      };
    }

    setFilters(newFilters);

    // Cập nhật URL params
    const newParams = new URLSearchParams(searchParams);
    if (key === "sort") {
      const [sortBy, sortOrder] = value.split("-");
      newParams.set("sortBy", sortBy);
      newParams.set("sortOrder", sortOrder);
    } else {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handlePageChange = (page) => {
    // Validate page number
    if (page < 1 || page > pagination.totalPages) {
      return;
    }

    const newFilters = {
      ...filters,
      page,
    };

    setFilters(newFilters);

    // Cập nhật URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
  };

  const handleSchoolClick = (school) => {
    navigate(`/schools/${school._id}`);
  };

  // Update inputPage when currentPage changes
  useEffect(() => {
    setInputPage(pagination.currentPage);
  }, [pagination.currentPage]);

  return (
    <div className="container">
      <div className="schools-list">
        <div className="schools-list__header">
          <h1 className="schools-list__title">Danh sách trường học</h1>
          {filters.search && (
            <p className="schools-list__search-info">
              Kết quả tìm kiếm cho: "<strong>{filters.search}</strong>"
            </p>
          )}
        </div>

        {/* Filters */}
        <div className="schools-list__filters">
          {/* Level Filter */}
          <div className="schools-list__filter-group">
            <label className="schools-list__filter-label">Cấp học:</label>
            <select
              value={filters.level}
              onChange={(e) => handleFilterChange("level", e.target.value)}
              className="schools-list__select"
            >
              {schoolLevels.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Filter */}
          <div className="schools-list__filter-group">
            <label className="schools-list__filter-label">Sắp xếp:</label>
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => handleFilterChange("sort", e.target.value)}
              className="schools-list__select"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <Link to="/schools/create" className="schools-list__add-btn btn">
            Thêm trường mới
          </Link>
        </div>

        {/* Results count */}
        <div className="schools-list__count">
          <span>
            Tìm thấy <span className="count-number">{pagination.total}</span>{" "}
            trường học
            {filters.search && (
              <>
                {" "}
                cho "<span className="search-term">{filters.search}</span>"
              </>
            )}
            {filters.level &&
              ` - ${
                schoolLevels.find((l) => l.value === filters.level)?.label
              }`}
          </span>
        </div>

        {loading ? (
          <div className="schools-list__loading">
            <div className="loading-spinner"></div>
            <p>Đang tải...</p>
          </div>
        ) : error ? (
          <div className="schools-list__error">
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn--primary"
            >
              Thử lại
            </button>
          </div>
        ) : schools.length === 0 ? (
          <div className="schools-list__empty">
            <h3>Không tìm thấy trường nào phù hợp</h3>
            <p>Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm</p>
          </div>
        ) : (
          <>
            {/* School Grid */}
            <div className="schools-list__grid">
              {schools.map((school) => (
                <SchoolCard
                  key={school._id}
                  school={{
                    ...school,
                    rating: school.ratings?.overall || 0,
                  }}
                  onClick={handleSchoolClick}
                />
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              onPageChange={handlePageChange}
              itemName="trường học"
            />
          </>
        )}
      </div>
    </div>
  );
}
